import { useEffect, useState } from "react";
import DashboardLayout from "../components/layout/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import {
  CheckCircle2, XCircle, AlertTriangle, Loader2, RotateCw, Rocket,
  ShieldCheck, ExternalLink,
} from "lucide-react";
import api from "../lib/api";
import { toast } from "sonner";

/**
 * /admin/stripe/preflight — one-click readiness check before launch.
 *
 * Renders a traffic-light status (green/yellow/red), the Stripe account
 * snapshot, and a checklist of each individual probe with detailed copy
 * the operator can act on directly.
 */
const STATUS_PALETTE = {
  green:  { ring: "border-emerald-500/40", bg: "bg-emerald-500/[0.06]", text: "text-emerald-300", icon: CheckCircle2 },
  yellow: { ring: "border-amber-400/40",   bg: "bg-amber-400/[0.06]",   text: "text-amber-300",  icon: AlertTriangle },
  red:    { ring: "border-red-500/40",     bg: "bg-red-500/[0.06]",     text: "text-red-300",    icon: XCircle },
};

const StripePreflightPage = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const run = async (showToast = false) => {
    setRefreshing(true);
    try {
      const r = await api.get("/admin/stripe/preflight");
      setData(r.data);
      if (showToast) {
        toast.success(
          { green: "All systems green — ready to launch.",
            yellow: "Mostly green with warnings — review below.",
            red:    "Blockers found — fix items in red." }[r.data.status]
        );
      }
    } catch (e) {
      console.error("Stripe preflight failed", e);
      toast.error(e.response?.data?.detail || "Could not run preflight.");
    } finally {
      setRefreshing(false);
      setLoading(false);
    }
  };

  useEffect(() => { run(false); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const palette = STATUS_PALETTE[data?.status] || STATUS_PALETTE.yellow;
  const StatusIcon = palette.icon;

  return (
    <DashboardLayout
      title="Stripe Live Preflight"
      subtitle="Pre-launch sanity check on the Stripe configuration of THIS environment."
    >
      <div className="space-y-6" data-testid="stripe-preflight-page">
        {loading ? (
          <div className="flex items-center text-slate-500 py-10">
            <Loader2 className="w-5 h-5 animate-spin mr-2" /> Running preflight…
          </div>
        ) : (
          <>
            {/* Status hero */}
            <Card className={`${palette.ring} ${palette.bg} border-2`} data-testid="preflight-status-card">
              <CardContent className="p-6 flex items-start gap-4">
                <StatusIcon className={`${palette.text} shrink-0 mt-1`} size={32} />
                <div className="flex-1 min-w-0">
                  <p className={`text-[10px] uppercase tracking-[0.3em] font-semibold mb-1 ${palette.text}`}>
                    Status · {data.status}
                  </p>
                  <h2 className="text-2xl font-serif text-white leading-snug">
                    {data.summary}
                  </h2>
                  <div className="flex items-center gap-2 mt-3 flex-wrap">
                    <Badge className="bg-platinum/10 text-platinum/80 border border-platinum/20 capitalize text-[10px]">
                      Key mode: {data.key_mode}
                    </Badge>
                    {data.account?.id && (
                      <Badge className="bg-platinum/10 text-platinum/80 border border-platinum/20 text-[10px] font-mono">
                        {data.account.id}
                      </Badge>
                    )}
                    {data.account?.business_name && (
                      <Badge className="bg-platinum/10 text-platinum/80 border border-platinum/20 text-[10px]">
                        {data.account.business_name}
                      </Badge>
                    )}
                  </div>
                </div>
                <Button
                  onClick={() => run(true)}
                  disabled={refreshing}
                  className="bg-amber-400 hover:bg-amber-300 text-slate-900 font-semibold shrink-0"
                  data-testid="preflight-refresh"
                >
                  {refreshing
                    ? <Loader2 size={14} className="mr-2 animate-spin" />
                    : <RotateCw size={14} className="mr-2" />}
                  Re-run
                </Button>
              </CardContent>
            </Card>

            {/* Checklist */}
            <Card className="border-platinum/10 bg-navy-light/30">
              <CardHeader>
                <CardTitle className="text-platinum text-base font-serif">Probe Results</CardTitle>
                <CardDescription className="text-platinum/60">
                  Each item below was tested live against your Stripe account just now.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-2">
                {data.checks.map((c) => {
                  const ok = c.ok;
                  const warn = !ok && c.level === "warning";
                  const Icon = ok ? CheckCircle2 : (warn ? AlertTriangle : XCircle);
                  const color = ok ? "text-emerald-300" : (warn ? "text-amber-300" : "text-red-300");
                  return (
                    <div
                      key={c.label}
                      className="flex items-start gap-3 rounded-md border border-platinum/10 bg-obsidian/40 p-3"
                      data-testid={`preflight-check-${c.label.replace(/\s+/g,'-').toLowerCase()}`}
                    >
                      <Icon className={`${color} shrink-0 mt-0.5`} size={16} />
                      <div className="flex-1 min-w-0">
                        <p className={`text-sm font-medium ${ok ? "text-slate-100" : color}`}>
                          {c.label}
                        </p>
                        <p className="text-xs text-slate-400 mt-0.5 leading-snug">{c.detail}</p>
                      </div>
                      {!ok && (
                        <Badge className={`text-[10px] uppercase ${warn ? "bg-amber-400/10 text-amber-300 border-amber-400/30" : "bg-red-500/10 text-red-300 border-red-500/30"} border`}>
                          {warn ? "Warning" : "Blocker"}
                        </Badge>
                      )}
                    </div>
                  );
                })}
              </CardContent>
            </Card>

            {/* Smoke-test reminder */}
            <Card className="border-amber-400/20 bg-amber-400/[0.04]">
              <CardContent className="p-5 flex items-start gap-3 text-sm text-slate-300 leading-relaxed">
                <Rocket className="text-amber-300 shrink-0 mt-0.5" size={18} />
                <div>
                  <p className="text-amber-200 font-semibold mb-1">Final 2-minute launch test</p>
                  <p>
                    Once this page is fully green, register one real member at{" "}
                    <code className="font-mono text-amber-200">/register</code> using YOUR own credit card.
                    Confirm the charge appears in Stripe Dashboard in <strong>Live</strong> mode, then refund yourself.
                    If the charge shows up and your account is created, you're truly live.
                  </p>
                </div>
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </DashboardLayout>
  );
};

export default StripePreflightPage;
